/**
 * Floataway damage numbers: DOM labels projected from world-space hit points.
 * They POP in (a scale overshoot that catches the eye), arc up-and-out (so a
 * burst of rapid hits FANS APART instead of stacking into an unreadable blob),
 * and fade over ~1 s. A heavy black stroke + colored glow keeps every number
 * legible over ANY background — bright sand, sky, water, muzzle flash — which is
 * the whole point: the hit feedback must never wash out. Heavy hits read bigger
 * and hotter. A small pool — combat is bursts, not spreadsheets.
 */
import * as THREE from "three";

const POOL = 24;
const LIFE = 0.95;
const RISE = 76; // px the number climbs over its life (eased, so it lofts then settles)
const POP_TIME = 0.12; // s of scale-overshoot on spawn — the eye-catch
const POP_SCALE = 1.7; // spawn overshoot scale before it settles to 1
const DRIFT = 34; // px of horizontal fan (± half each side) so stacked hits separate
const BIG_DAMAGE = 22; // at/above this a hit reads as "heavy": hotter color + bigger
const NEAR_D = 14; // ≤ this many metres a hit punches up to NEAR_SCALE
const FAR_D = 70; // ≥ this many metres it sits at base size (never SMALLER — always legible)
const NEAR_SCALE = 1.3;

interface Num {
  el: HTMLElement;
  pos: THREE.Vector3; // system space, at the LATEST sim tick (carryBodies advances it)
  prev: THREE.Vector3; // same point at the PREVIOUS tick — update() lerps prev→pos
  t: number;
  vx: number; // horizontal fan (px over life)
  base: number; // severity scale (heavy hits start bigger)
}

export class DamageNumbers {
  private nums: Num[] = [];
  private next = 0;
  private v = new THREE.Vector3();
  private camPos = new THREE.Vector3();

  constructor(root: HTMLElement) {
    for (let i = 0; i < POOL; i++) {
      const el = document.createElement("div");
      el.className = "dmg-num";
      el.style.display = "none";
      root.appendChild(el);
      this.nums.push({ el, pos: new THREE.Vector3(), prev: new THREE.Vector3(), t: 0, vx: 0, base: 1 });
    }
  }

  spawn(point: [number, number, number], amount: number): void {
    const n = this.nums[this.next];
    this.next = (this.next + 1) % POOL;
    n.pos.set(...point);
    n.prev.copy(n.pos); // no ghost delta on the first rendered frame
    n.t = LIFE;
    const dmg = Math.max(1, Math.round(amount));
    const big = dmg >= BIG_DAMAGE;
    n.el.textContent = String(dmg);
    n.el.className = big ? "dmg-num big" : "dmg-num";
    n.base = big ? 1.3 : 1; // heavier hits start bigger
    n.vx = (Math.random() * 2 - 1) * DRIFT; // fan direction so a flurry doesn't overlap
    n.el.style.display = "block";
    n.el.style.opacity = "1";
  }

  /** Per frame: project to screen, pop, arc up-and-out, fade. */
  private carryTmp = new THREE.Vector3();
  private carryAx = new THREE.Vector3();
  private carryQ = new THREE.Quaternion();

  /** Ride rotating/orbiting bodies (2026-08-02, user report: "flying damage isn't synced with
   *  the planet's movement") — same carry as fx/bolts.carryBodies: a number spawned over a
   *  spinning planet must stay over the wound, not drift off sideways in system space.
   *  Called once per FIXED sim tick, right after tickPoses. `prev` snapshots the pre-carry
   *  position so update() can lerp prev→pos with the render alpha: planets RENDER at an
   *  alpha-interpolated pose between ticks (starsystem.update), so a number projected at the
   *  full-tick pose leads the ground by a varying sub-step every frame — visible jitter. */
  carryBodies(planets: readonly { pose: { center: THREE.Vector3; spinAngle: number }; posePrev: { center: THREE.Vector3; spinAngle: number }; def: { radius: number; spin: { axis: [number, number, number] } } }[]): void {
    for (const n of this.nums) {
      if (n.t <= 0) continue;
      n.prev.copy(n.pos); // this tick's baseline — BEFORE the carry moves pos
      let best: (typeof planets)[number] | null = null;
      let bestD = Infinity;
      for (const p of planets) {
        const d = n.pos.distanceTo(p.pose.center) - p.def.radius;
        if (d < bestD) { bestD = d; best = p; }
      }
      if (!best) continue;
      const R = best.def.radius;
      const k = Math.max(0, Math.min(1, (2.6 * R - (bestD + R)) / (0.6 * R)));
      if (k <= 0) continue;
      const dA = (best.pose.spinAngle - best.posePrev.spinAngle) * k;
      this.carryAx.set(best.def.spin.axis[0], best.def.spin.axis[1], best.def.spin.axis[2]);
      this.carryQ.setFromAxisAngle(this.carryAx, dA);
      n.pos.sub(best.posePrev.center).applyQuaternion(this.carryQ).add(best.posePrev.center);
      this.carryTmp.copy(best.pose.center).sub(best.posePrev.center);
      n.pos.addScaledVector(this.carryTmp, k);
    }
  }

  update(dt: number, camera: THREE.PerspectiveCamera, origin: THREE.Vector3, alpha = 1): void {
    camera.getWorldPosition(this.camPos);
    for (const n of this.nums) {
      if (n.t <= 0) continue;
      n.t -= dt;
      if (n.t <= 0) {
        n.el.style.display = "none";
        continue;
      }
      // Same sub-tick interpolation as the planets/character render pose — projecting the
      // raw full-tick pos against the lerped world reads as per-frame jitter.
      this.v.lerpVectors(n.prev, n.pos, alpha).sub(origin); // → render space (floating origin)
      const dist = this.v.distanceTo(this.camPos);
      this.v.project(camera);
      if (this.v.z > 1 || this.v.z < -1) {
        n.el.style.display = "none"; // behind camera / beyond far plane
        continue;
      }
      const age = LIFE - n.t; // 0 → LIFE
      const k = age / LIFE; // 0 → 1 over the lifetime
      const riseK = 1 - (1 - k) * (1 - k); // ease-out: lofts fast, settles
      const x = (this.v.x * 0.5 + 0.5) * window.innerWidth + n.vx * riseK;
      const y = (-this.v.y * 0.5 + 0.5) * window.innerHeight - riseK * RISE;
      // Pop overshoot on spawn, then a slight shrink as it dies — reads as "impact".
      const pop = age < POP_TIME ? POP_SCALE + (1 - POP_SCALE) * (age / POP_TIME) : 1;
      const die = k > 0.75 ? 1 - (k - 0.75) * 0.4 : 1;
      // Near hits punch bigger; far hits sit at base (never smaller) so they stay readable.
      const distScale = THREE.MathUtils.clamp(
        THREE.MathUtils.mapLinear(dist, NEAR_D, FAR_D, NEAR_SCALE, 1),
        1,
        NEAR_SCALE,
      );
      const scale = n.base * pop * die * distScale;
      n.el.style.transform =
        `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -100%) scale(${scale.toFixed(3)})`;
      // Hold full opacity, then fade over the last ~45% of life.
      n.el.style.opacity = k < 0.55 ? "1" : String(Math.max(0, 1 - (k - 0.55) / 0.45));
    }
  }
}
